import { Component, input, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { IVerifyResetCode_Request } from "../../../Utils/_services/account/account.model";
import { AccountService } from "../../../Utils/_services/account/account.service";

@Component({
  selector: "app-verify-code",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./verify-code.component.html",
  styleUrl: "./verify-code.component.css",
})
export class VerifyCodeComponent {
  constructor(
    private router: Router,
    private toast: ToastrService,
    private accountService: AccountService
  ) {}

  email = input.required<string>();
  isVerifying = signal(false);
  isResendingCode = signal(false);

  code = signal<string[]>(["", "", "", "", "", ""]);
  timeRemaining = signal<string>("04:57");

  private timer: any;

  ngOnInit() {
    this.startTimer();
  }

  ngOnDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  onInput(event: any, index: number) {
    const value = event.target.value;
    // ✅ ADD THESE 3 LINES - Filter out non-digits
    const numericValue = value.replace(/\D/g, "");
    event.target.value = numericValue;

    if (value.length > 1) {
      event.target.value = value.slice(-1);
    }

    const newCode = [...this.code()];
    newCode[index] = event.target.value;
    this.code.set(newCode);

    // Auto-focus next input
    if (event.target.value && index < 5) {
      const nextInput =
        event.target.parentElement.nextElementSibling?.querySelector("input");
      nextInput?.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    // Handle backspace to move to previous input
    if (event.key === "Backspace" && !this.code()[index] && index > 0) {
      const prevInput = (
        event.target as HTMLElement
      ).parentElement?.previousElementSibling?.querySelector("input");
      prevInput?.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData("text");

    if (pastedData && /^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      this.code.set(digits);

      // Fill all inputs
      const inputs = document.querySelectorAll(".code-input input");
      digits.forEach((digit, i) => {
        if (inputs[i]) {
          (inputs[i] as HTMLInputElement).value = digit;
        }
      });

      // Focus last input
      (inputs[5] as HTMLInputElement)?.focus();
      if (this.code().length == 6) {
        this.verifyCode();
      }
    }
  }

  verifyCode() {
    this.isVerifying.set(true);
    const fullCode = this.code().join("");
    if (fullCode.length === 6) {
      const req: IVerifyResetCode_Request = {
        email: this.email(),
        verificationCode: this.code().join(""),
      };

      this.accountService
        .verifyResetCode(req)
        .pipe(
          finalize(() => {
            this.isVerifying.set(false);
          })
        )
        .subscribe({
          next: res => {
            if (res.data?.codeVerified) {
              this.router.navigate(["/auth/reset-password"], {
                state: {
                  email: this.email(),
                  code: this.code().join(""),
                },
              });
            }
            else{
              this.toast.error(res.msg);
            }
          },
          error: (res) => {
            this.toast.error(res.error.msg);
          }
        });
    } else {
      this.toast.error("Please enter complete code");
    }
  }

  resendCode() {
    this.isResendingCode.set(true);
    this.accountService
      .forgot({ email: this.email() })
      .pipe(
        finalize(() => {
          this.isResendingCode.set(false);
        })
      )
      .subscribe({
        next: res => {
          if (res.data.emailSent) {
            this.code.set(["", "", "", "", "", ""]);
            const inputs = document.querySelectorAll(".code-input input");
            inputs.forEach(input => {
              (input as HTMLInputElement).value = "";
            });
            (inputs[0] as HTMLInputElement)?.focus();
            this.timeRemaining.set("04:57");
          }
        },
        error: err => {},
      });
  }

  private startTimer() {
    let seconds = 297; // 4:57 = 297 seconds

    this.timer = setInterval(() => {
      seconds--;

      if (seconds <= 0) {
        clearInterval(this.timer);
        this.timeRemaining.set("00:00");
        return;
      }

      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      this.timeRemaining.set(
        `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`
      );
    }, 1000);
  }
}
